import { scoreRefs } from "@/lib/ref-scoring";
import type { WeeklyPointSummaryConfig, WidgetWeekEntry } from "@/lib/schema";
import type { WidgetWeekViewProps } from "@/widgets/types";

type Props = WidgetWeekViewProps<WeeklyPointSummaryConfig>;

interface RowSums {
  expected: number;
  current: number;
  previous: number;
  previousExpected: number;
  hasCurrentRefs: boolean;
  hasPreviousRefs: boolean;
}

function computeRow(
  widgetIds: string[],
  thisEntries: WidgetWeekEntry[],
  prevEntries: WidgetWeekEntry[] | null,
): RowSums {
  const cur = scoreRefs(widgetIds, thisEntries);
  const prev = prevEntries
    ? scoreRefs(widgetIds, prevEntries)
    : { score: 0, expected: 0, matched: false };
  return {
    expected: cur.expected,
    current: cur.score,
    previous: prev.score,
    previousExpected: prev.expected,
    hasCurrentRefs: cur.matched,
    hasPreviousRefs: prev.matched,
  };
}

function pctValue(num: number, denom: number): number | null {
  if (denom <= 0) return null;
  return Math.round((num / denom) * 100);
}

function goalClass(pct: number | null): "up" | "down" | null {
  if (pct === null) return null;
  return pct >= 100 ? "up" : "down";
}

function CurrentCell({
  rowId,
  current,
  expected,
}: {
  rowId: string;
  current: number;
  expected: number;
}) {
  const pct = pctValue(current, expected);
  const cls = goalClass(pct);
  return (
    <td className="td-now">
      <b>
        <span data-testid={`weekly-point-summary-current-${rowId}`}>
          {current}p
        </span>
      </b>
      {pct !== null && (
        <>
          {" ("}
          <span
            data-testid={`weekly-point-summary-current-pct-${rowId}`}
            className={cls ?? undefined}
          >
            {pct}%
          </span>
          {")"}
        </>
      )}
    </td>
  );
}

function PreviousCell({
  rowId,
  hasData,
  previous,
  previousExpected,
}: {
  rowId: string;
  hasData: boolean;
  previous: number;
  previousExpected: number;
}) {
  if (!hasData) {
    return (
      <td className="td-prev dim">
        <span data-testid={`weekly-point-summary-previous-${rowId}`}>—</span>
      </td>
    );
  }
  const pct = pctValue(previous, previousExpected);
  return (
    <td className="td-prev dim">
      <span data-testid={`weekly-point-summary-previous-${rowId}`}>
        {previous}p
      </span>
      {pct !== null && (
        <>
          {" ("}
          <span data-testid={`weekly-point-summary-previous-pct-${rowId}`}>
            {pct}%
          </span>
          {")"}
        </>
      )}
    </td>
  );
}

export function WeekView({ config, allWidgets, previousWeek }: Props) {
  const prevEntries = previousWeek ? previousWeek.widgets : null;

  const rowSums = config.rows.map((row) => ({
    row,
    sums: computeRow(row.widgetIds, allWidgets, prevEntries),
  }));

  const totalExpected = rowSums.reduce((s, r) => s + r.sums.expected, 0);
  const totalCurrent = rowSums.reduce((s, r) => s + r.sums.current, 0);
  const totalPrevious = rowSums.reduce((s, r) => s + r.sums.previous, 0);
  const totalPreviousExpected = rowSums.reduce(
    (s, r) => s + r.sums.previousExpected,
    0,
  );
  const anyPreviousRefs =
    !!previousWeek && rowSums.some((r) => r.sums.hasPreviousRefs);
  const totalPct = pctValue(totalCurrent, totalExpected);
  const totalPrevPct = pctValue(totalPrevious, totalPreviousExpected);
  const totalGoalCls = goalClass(totalPct);

  return (
    <section className="widget-card" data-testid={`widget-week-${config.id}`}>
      <div className="widget-card-head">
        <h3>{config.label}</h3>
      </div>
      {config.rows.length === 0 ? (
        <p
          className="hint"
          data-testid={`weekly-point-summary-empty-${config.id}`}
        >
          Ingen rader konfigurert.
        </p>
      ) : (
        <div className="week-matrix">
          <table data-testid={`weekly-point-summary-table-${config.id}`}>
            <thead>
              <tr>
                <th className="th-group">Oppgave</th>
                <th>Forvented</th>
                <th>Denne uken</th>
                <th>Forrige uke</th>
              </tr>
            </thead>
            <tbody>
              {rowSums.map(({ row, sums }) => (
                <tr
                  key={row.id}
                  data-testid={`weekly-point-summary-row-${config.id}-${row.id}`}
                >
                  <td
                    className="td-label"
                    data-testid={`weekly-point-summary-name-${row.id}`}
                  >
                    {row.name || "(uten navn)"}
                  </td>
                  <td
                    className="td-goal"
                    data-testid={`weekly-point-summary-expected-${row.id}`}
                  >
                    {sums.expected}p
                  </td>
                  <CurrentCell
                    rowId={row.id}
                    current={sums.current}
                    expected={sums.expected}
                  />
                  <PreviousCell
                    rowId={row.id}
                    hasData={!!previousWeek && sums.hasPreviousRefs}
                    previous={sums.previous}
                    previousExpected={sums.previousExpected}
                  />
                </tr>
              ))}
              <tr
                className="row-total"
                data-testid={`weekly-point-summary-total-${config.id}`}
              >
                <td className="td-label">Totalt</td>
                <td
                  className="td-goal"
                  data-testid={`weekly-point-summary-total-expected-${config.id}`}
                >
                  {totalExpected}p
                </td>
                <td className="td-now">
                  <b>
                    <span
                      data-testid={`weekly-point-summary-total-current-${config.id}`}
                    >
                      {totalCurrent}p
                    </span>
                  </b>
                  {totalPct !== null && (
                    <>
                      {" ("}
                      <span
                        data-testid={`weekly-point-summary-total-current-pct-${config.id}`}
                        className={totalGoalCls ?? undefined}
                      >
                        {totalPct}%
                      </span>
                      {")"}
                    </>
                  )}
                </td>
                <td className="td-prev dim">
                  {anyPreviousRefs ? (
                    <>
                      <span
                        data-testid={`weekly-point-summary-total-previous-${config.id}`}
                      >
                        {totalPrevious}p
                      </span>
                      {totalPrevPct !== null && (
                        <>
                          {" ("}
                          <span
                            data-testid={`weekly-point-summary-total-previous-pct-${config.id}`}
                          >
                            {totalPrevPct}%
                          </span>
                          {")"}
                        </>
                      )}
                    </>
                  ) : (
                    <span
                      data-testid={`weekly-point-summary-total-previous-${config.id}`}
                    >
                      —
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
